import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, BookmarkPlus } from "lucide-react";

/**
 * Templates dialog — two sections:
 *   1. "My Templates" (user-saved on the server, keyed by owner_id)
 *   2. "Default Templates" (built-in shortcuts)
 *
 * Also offers a "Save current setup as template" input so the teacher can
 * bookmark whatever they've configured and reuse it across classes / sections.
 */
export default function TemplatesDialog({
  open, onClose,
  templates, onApply, categoryPlural,
  myTemplates, onSaveTemplate, onDeleteTemplate,
}) {
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const handleSave = async () => {
    const name = saveName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await onSaveTemplate(name);
      setSaveName("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="gs-templates-dialog">
      <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-heading text-2xl font-bold mb-1">Grading Templates</h2>
        <p className="text-sm text-brand-inkMuted mb-4">
          Save your current setup or apply one to populate periods, scale, and {categoryPlural.toLowerCase()}. You can still edit everything afterward.
        </p>

        {/* Save current setup as template */}
        <div className="border-2 border-brand-purple/20 rounded-2xl p-4 mb-5 bg-brand-purpleLight/20" data-testid="gs-save-template">
          <p className="font-heading font-semibold text-foreground text-sm mb-2 flex items-center gap-2">
            <BookmarkPlus className="w-4 h-4 text-brand-purple" />
            Save current setup as a template
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder='e.g. "Playgroup Custom Scale"'
              className="rounded-xl h-10 flex-1"
              data-testid="gs-save-template-name"
              disabled={saving}
            />
            <Button
              onClick={handleSave}
              disabled={saving || !saveName.trim()}
              className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white"
              data-testid="gs-save-template-btn"
            >
              {saving ? "Saving..." : "Save Template"}
            </Button>
          </div>
          <p className="text-xs text-brand-inkMuted mt-1.5">
            Templates are saved on the server for your school and can be applied to any class or section.
          </p>
        </div>

        {/* My saved templates */}
        {myTemplates?.length > 0 && (
          <div className="mb-5">
            <p className="font-heading font-semibold text-foreground text-sm mb-2">My Templates ({myTemplates.length})</p>
            <div className="space-y-2">
              {myTemplates.map((tpl) => (
                <div key={tpl.id} className="border border-brand-purple/15 rounded-2xl p-3 flex items-start justify-between gap-3 hover:bg-brand-purpleLight/30" data-testid={`gs-my-tpl-${tpl.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-heading font-semibold text-foreground truncate">{tpl.name}</p>
                    <p className="text-xs text-brand-inkMuted mt-0.5">
                      {tpl.periods?.length || 0} period{(tpl.periods?.length || 0) === 1 ? "" : "s"} ·{" "}
                      {tpl.categories?.length || 0} {categoryPlural.toLowerCase()} ·{" "}
                      {tpl.settings?.grading_mode === "direct" ? "Direct" : "Calculated"} ·{" "}
                      Scale: {tpl.settings?.grading_scale_type || "percentage"}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      onClick={() => onApply(tpl)}
                      className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white"
                      data-testid={`gs-my-tpl-apply-${tpl.id}`}
                    >
                      Apply
                    </Button>
                    <Button
                      onClick={() => {
                        if (window.confirm(`Delete template "${tpl.name}"? This cannot be undone.`)) {
                          onDeleteTemplate(tpl.id);
                        }
                      }}
                      variant="ghost"
                      size="icon"
                      className="rounded-2xl text-[#C62828] hover:bg-red-50"
                      data-testid={`gs-my-tpl-delete-${tpl.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Default templates */}
        <div>
          <p className="font-heading font-semibold text-foreground text-sm mb-2">Default Templates</p>
          <div className="space-y-3">
            {templates.map((tpl) => (
              <div key={tpl.key} className="border border-brand-purple/15 rounded-2xl p-4 flex items-start justify-between gap-3 hover:bg-brand-purpleLight/30" data-testid={`gs-tpl-${tpl.key}`}>
                <div>
                  <p className="font-heading font-semibold text-foreground">{tpl.name}</p>
                  <p className="text-sm text-brand-inkMuted mt-0.5">{tpl.description}</p>
                </div>
                <Button onClick={() => onApply(tpl)} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid={`gs-tpl-apply-${tpl.key}`}>Apply</Button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button variant="outline" onClick={onClose} className="rounded-2xl">Close</Button>
        </div>
      </div>
    </div>
  );
}
